import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Box, Typography, Stack, Link } from '@mui/material'
import { ContentContainer, Divider, SplitHalf, WorkforceSection, WrapperStyled, useAuth, track, eventTypes } from 'torqcloud-ui'
import { appConfig } from '@/config'
import Button from '@/components/Button/Button'

type OrgStatus = 'no_org' | 'owner_inactive' | 'member_inactive'

interface OrganizationInactivePageProps {
  status: OrgStatus
  organizationName?: string
}

export default function OrganizationInactivePage({ status, organizationName }: OrganizationInactivePageProps) {
  const { logout } = useAuth()

  useEffect(() => {
    track(eventTypes.ORGANIZATION_INACTIVE_VIEWED, { status })
  }, [status])

  const handleContactSupport = () => {
    track(eventTypes.SUPPORT_CONTACTED, { status, source: 'organization_inactive' })
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:open'])
    }
  }

  const handleBillingPortal = () => {
    track(eventTypes.BILLING_PORTAL_OPENED, { status, source: 'organization_inactive' })
    if (appConfig.paymentPortalUrl) {
      window.location.href = appConfig.paymentPortalUrl
    }
  }

  return (
    <WrapperStyled>
      <Helmet>
        <title>{`Organization Status | ${appConfig.APP_NAME}`}</title>
      </Helmet>
      <Box style={{ ...ContentContainer }}>
        <WorkforceSection />
        <Divider />
        <Box sx={{ ...SplitHalf, right: 0, margin: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: 3,
            }}
          >
            <Stack spacing={3} sx={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
              {status === 'no_org' && (
                <>
                  <Typography variant="h5">No Organization</Typography>
                  <Typography variant="body1" color="text.secondary">
                    You don't yet belong to a Workforce Organization. You can sign up for a new organization via the Billing Portal. If you
                    believe you should already be a part of an organization please contact support.
                  </Typography>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button variant="secondary" onClick={handleContactSupport}>
                      Contact Support
                    </Button>
                    <Button variant="primary" onClick={handleBillingPortal}>
                      Billing Portal
                    </Button>
                  </Stack>
                </>
              )}

              {status === 'owner_inactive' && (
                <>
                  <Typography variant="h5">Organization Inactive</Typography>
                  <Typography variant="body1" color="text.secondary">
                    Your Organization ({organizationName}) is not currently active. To Activate your Organization sign up for a plan via the
                    Billing Portal. If you believe your Organization should already be active please contact support.
                  </Typography>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button variant="secondary" onClick={handleContactSupport}>
                      Contact Support
                    </Button>
                    <Button variant="primary" onClick={handleBillingPortal}>
                      Billing Portal
                    </Button>
                  </Stack>
                </>
              )}

              {status === 'member_inactive' && (
                <>
                  <Typography variant="h5">Organization Inactive</Typography>
                  <Typography variant="body1" color="text.secondary">
                    You are a member of the {organizationName} Organization, but it is currently inactive. Please contact your Organization
                    owner to resolve this issue. If you believe you have received this message in error please contact support.
                  </Typography>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button variant="primary" onClick={handleContactSupport}>
                      Contact Support
                    </Button>
                  </Stack>
                </>
              )}

              <Link
                component="button"
                variant="body2"
                color="text.secondary"
                onClick={() => void logout()}
                sx={{ mt: 2, cursor: 'pointer' }}
              >
                Log out
              </Link>
            </Stack>
          </Box>
        </Box>
      </Box>
    </WrapperStyled>
  )
}
